import { Star } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

type DisplayProps = {
  value: number;
  size?: number;
  className?: string;
  interactive?: false;
};

type InteractiveProps = {
  value: number;
  onChange: (value: number) => void;
  size?: number;
  className?: string;
  interactive: true;
};

type Props = DisplayProps | InteractiveProps;

/** Εμφάνιση αστεριών (με μισά αστέρια) ή επιλογή βαθμολογίας 1–5. */
export function RatingStars(props: Props) {
  const { value, size = 18, className } = props;
  const [hover, setHover] = useState(0);

  if (props.interactive) {
    const active = hover || value;
    return (
      <div className={cn("flex items-center gap-1", className)} role="radiogroup" aria-label="Βαθμολογία">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} αστέρια`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => props.onChange(n)}
            className="rounded p-0.5 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Star
              style={{ width: size + 6, height: size + 6 }}
              className={cn(
                "transition-colors",
                n <= active ? "fill-amber-400 text-amber-400" : "fill-muted text-muted-foreground/40",
              )}
            />
          </button>
        ))}
      </div>
    );
  }

  const full = Math.floor(value);
  const frac = value - full;
  return (
    <div
      className={cn("inline-flex items-center gap-0.5", className)}
      aria-label={`Βαθμολογία ${value} στα 5`}
      title={`${value} / 5`}
    >
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = i < full ? 1 : i === full ? frac : 0;
        return (
          <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
            <Star
              style={{ width: size, height: size }}
              className="absolute inset-0 fill-muted text-muted-foreground/30"
            />
            {fill > 0 && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${fill * 100}%` }}
              >
                <Star style={{ width: size, height: size }} className="fill-amber-400 text-amber-400" />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}